// Extracted from ProductsPage.js. Veneer size/code
// selector dialog — multi-code veneers pick a code first, then size
// (8x4 / 10x4) and sheet quantity.
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Plus } from 'lucide-react';
import { formatINR } from '../../../lib/store';

export function VeneerSizeSelectorDialog({
  group,
  onClose,
  selectedCode,
  setSelectedCode,
  selectedSize,
  setSelectedSize,
  quantityInput,
  setQuantityInput,
  setQuantity,
  onAdd,
}) {
  return (
    <Dialog open={!!group} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="border-heda-line bg-white sm:rounded-3xl w-[95vw] max-w-md max-h-[85vh] overflow-hidden flex flex-col" data-testid="veneer-size-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-heda-ink">
            {group?.veneer_codes?.length > 1 ? 'Select Code, Size & Add' : 'Select Size & Add'}
          </DialogTitle>
        </DialogHeader>

        {group && (
          <div className="space-y-3 overflow-y-auto flex-1 pr-1">
            {/* Product Info - Compact */}
            <div className="p-2 bg-heda-gold-tint rounded-xl border border-amber-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-heda-ink text-sm">{group.brand}</p>
                  <p className="text-xs text-heda-sub">{group.name}</p>
                </div>
                <div className="flex gap-1">
                  {/* Show selected code or single code */}
                  {selectedCode ? (
                    <span className="text-xs font-mono bg-amber-50 text-amber-700 px-2 py-0.5 rounded-lg">
                      {group.code_prefix && selectedCode.code
                        ? `${group.code_prefix}-${selectedCode.code}`
                        : selectedCode.code}
                    </span>
                  ) : group.product_code && (!group.veneer_codes || group.veneer_codes.length <= 1) && (
                    <span className="text-xs font-mono bg-amber-50 text-amber-700 px-2 py-0.5 rounded-lg">
                      {group.code_prefix && group.product_code
                        ? `${group.code_prefix}-${group.product_code}`
                        : group.product_code}
                    </span>
                  )}
                  {group.veneer_type && (
                    <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700">
                      {group.veneer_type}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Code Selection - Only show if multiple codes */}
            {group.veneer_codes && group.veneer_codes.length > 1 && (
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Select Code</Label>
                <div className="mt-1 max-h-32 overflow-y-auto border border-heda-line rounded-xl">
                  {group.veneer_codes.map((vc, vcIdx) => {
                    const isSelected = selectedCode?.code === vc.code;
                    const has8x4 = vc.rate_8x4 > 0;
                    const has10x4 = vc.rate_10x4 > 0;
                    const hasStock = vc.stock_sheets > 0;
                    const availableSizes = [];
                    if (has8x4) availableSizes.push('8x4');
                    if (has10x4) availableSizes.push('10x4');
                    // Build full code with prefix: LH-EP101
                    const fullCode = group.code_prefix && vc.code
                      ? `${group.code_prefix}-${vc.code}`
                      : vc.code;

                    return (
                      <button
                        key={vcIdx}
                        onClick={() => {
                          setSelectedCode(vc);
                          if (has8x4) setSelectedSize('8x4');
                          else if (has10x4) setSelectedSize('10x4');
                        }}
                        className={`w-full flex items-center justify-between px-2 py-1.5 border-b border-heda-line last:border-b-0 transition-all text-xs ${
                          isSelected ? 'bg-heda-ink text-white' : 'bg-white hover:bg-heda-gold-tint'
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className={`font-mono font-bold ${isSelected ? 'text-white' : 'text-heda-ink'}`}>{fullCode}</span>
                          {hasStock ? (
                            <span className={`px-1 py-0.5 rounded-lg ${isSelected ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-700'}`}>
                              {vc.stock_sheets}
                            </span>
                          ) : (
                            <span className={`px-1 py-0.5 rounded-lg ${isSelected ? 'bg-white/20 text-white/70' : 'bg-heda-cream text-heda-mute'}`}>-</span>
                          )}
                          <span className={`${isSelected ? 'text-white/80' : 'text-heda-mute'}`}>({availableSizes.join(',')})</span>
                        </div>
                        <span className={`font-mono-data ${isSelected ? 'text-white' : 'text-heda-olive-dark'}`}>
                          {has8x4 && `₹${vc.rate_8x4}`}{has8x4 && has10x4 && '/'}{has10x4 && `₹${vc.rate_10x4}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Size Selection - Compact */}
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Select Size</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {(() => {
                  const rate8x4 = selectedCode?.rate_8x4 || group.rate_8x4;
                  const rate10x4 = selectedCode?.rate_10x4 || group.rate_10x4;
                  const has8x4 = group.veneer_codes?.length > 1 ? (selectedCode?.rate_8x4 > 0) : rate8x4 > 0;
                  const has10x4 = group.veneer_codes?.length > 1 ? (selectedCode?.rate_10x4 > 0) : rate10x4 > 0;

                  return (
                    <>
                      {has8x4 && (
                        <button
                          onClick={() => setSelectedSize('8x4')}
                          className={`p-2 rounded-xl border transition-all text-center ${
                            selectedSize === '8x4' ? 'border-amber-700 bg-amber-50' : 'border-heda-line hover:border-amber-200'
                          }`}
                        >
                          <p className="font-medium text-sm">8x4 ft</p>
                          <p className="text-xs text-heda-sub">32 sqft</p>
                          <p className="text-sm font-mono-data text-heda-olive-dark font-bold">{formatINR(selectedCode?.rate_8x4 || rate8x4)}/sqft</p>
                        </button>
                      )}
                      {has10x4 && (
                        <button
                          onClick={() => setSelectedSize('10x4')}
                          className={`p-2 rounded-xl border transition-all text-center ${
                            selectedSize === '10x4' ? 'border-amber-700 bg-amber-50' : 'border-heda-line hover:border-amber-200'
                          }`}
                        >
                          <p className="font-medium text-sm">10x4 ft</p>
                          <p className="text-xs text-heda-sub">40 sqft</p>
                          <p className="text-sm font-mono-data text-heda-olive-dark font-bold">{formatINR(selectedCode?.rate_10x4 || rate10x4)}/sqft</p>
                        </button>
                      )}
                      {!has8x4 && !has10x4 && group.veneer_codes?.length > 1 && (
                        <p className="col-span-2 text-center text-xs text-heda-mute py-2">Select a code first</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Quantity - Compact */}
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Quantity (Sheets)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button variant="outline" size="sm" onClick={() => {
                  const newQty = Math.max(1, (parseInt(quantityInput) || 1) - 1);
                  setQuantity(newQty);
                  setQuantityInput(String(newQty));
                }} className="rounded-xl h-9 w-9 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold">-</Button>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={quantityInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setQuantityInput(val);
                    if (val) setQuantity(parseInt(val) || 1);
                  }}
                  className="rounded-xl bg-heda-cream/50 text-center h-9 w-16 font-bold"
                />
                <Button variant="outline" size="sm" onClick={() => {
                  const newQty = (parseInt(quantityInput) || 1) + 1;
                  setQuantity(newQty);
                  setQuantityInput(String(newQty));
                }} className="rounded-xl h-9 w-9 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold">+</Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Button - Fixed at bottom */}
        {group && (
          <div className="flex-shrink-0 pt-3 border-t border-heda-line mt-2">
            <Button
              onClick={() => onAdd(group, selectedSize, parseInt(quantityInput) || 1, selectedCode)}
              disabled={group.veneer_codes?.length > 1 && !selectedCode}
              className="rounded-xl w-full bg-heda-ink hover:bg-black text-white uppercase tracking-wider font-semibold h-10 disabled:opacity-50"
              data-testid="confirm-veneer-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add to Quotation
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
